import React, { useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Avatar,
  IconButton,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';

const CompanyInfoForm = ({
  companyInfo,
  loading,
  saving,
  hasChanges,
  onChange,
  onSave,
  onReset,
  onLogoUpload,
  onSealUpload,
  logoBlobUrl,
  sealBlobUrl,
}) => {
  const logoInputRef = useRef(null);
  const sealInputRef = useRef(null);

  // 결산월 옵션
  const fiscalMonthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}월`
  }));

  // 파일 선택 핸들러
  const handleFileSelect = (e, uploadHandler) => {
    const file = e.target.files[0];
    if (file) {
      uploadHandler(file);
    }
    e.target.value = ''; // 같은 파일 재선택 허용
  };

  // 사업자등록번호 포맷팅
  const formatBizNo = (value) => {
    if (!value) return '';
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      {/* 헤더 */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          회사 정보 설정
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {hasChanges && (
            <Chip 
              label="변경사항 있음" 
              color="warning" 
              size="small" 
            />
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onReset}
            disabled={saving || !hasChanges}
          >
            초기화
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={onSave}
            disabled={saving || !hasChanges}
            color="primary"
          >
            {saving ? '저장 중...' : '저장'}
          </Button>
        </Box>
      </Box>

      {/* 안내 메시지 */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          • 이 정보는 견적서, 발주서, 세금계산서 등 각종 문서에 자동으로 사용됩니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • 사업자등록번호, 대표자명 등 정확한 정보를 입력해주세요.
        </Typography>
      </Box>

      {/* 기본 정보 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" color="primary" />
            기본 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="회사명"
                value={companyInfo?.companyNm || ''}
                onChange={(e) => onChange('companyNm', e.target.value)}
                placeholder="(주)회사명"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="회사명 (영문)"
                value={companyInfo?.companyNmEn || ''}
                onChange={(e) => onChange('companyNmEn', e.target.value)}
                placeholder="Company Name Co., Ltd."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="사업자등록번호"
                value={companyInfo?.bizNo || ''}
                onChange={(e) => onChange('bizNo', formatBizNo(e.target.value))}
                placeholder="000-00-00000"
                inputProps={{ maxLength: 12 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="법인등록번호"
                value={companyInfo?.corpNo || ''}
                onChange={(e) => onChange('corpNo', e.target.value)}
                placeholder="000000-0000000"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="업태"
                value={companyInfo?.bizType || ''}
                onChange={(e) => onChange('bizType', e.target.value)}
                placeholder="서비스업, 제조업 등"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="종목"
                value={companyInfo?.bizItem || ''}
                onChange={(e) => onChange('bizItem', e.target.value)}
                placeholder="소프트웨어 개발, 컨설팅 등"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="설립일"
                value={companyInfo?.establishmentDt || ''}
                onChange={(e) => onChange('establishmentDt', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="결산월"
                value={companyInfo?.fiscalMonth || 12}
                onChange={(e) => onChange('fiscalMonth', e.target.value)}
              >
                {fiscalMonthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 대표자 정보 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="primary" />
            대표자 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="대표자명"
                value={companyInfo?.ceoNm || ''}
                onChange={(e) => onChange('ceoNm', e.target.value)}
                placeholder="홍길동"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="대표자명 (영문)"
                value={companyInfo?.ceoNmEn || ''}
                onChange={(e) => onChange('ceoNmEn', e.target.value)}
                placeholder="Gildong Hong"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 연락처 정보 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon fontSize="small" color="primary" />
            연락처 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="대표 전화번호"
                value={companyInfo?.telNo || ''}
                onChange={(e) => onChange('telNo', e.target.value)}
                placeholder="02-1234-5678"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="대표 팩스번호"
                value={companyInfo?.faxNo || ''}
                onChange={(e) => onChange('faxNo', e.target.value)}
                placeholder="02-1234-5679"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="대표 이메일"
                type="email"
                value={companyInfo?.email || ''}
                onChange={(e) => onChange('email', e.target.value)}
                placeholder="info@company.co.kr"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="홈페이지 URL"
                value={companyInfo?.homepageUrl || ''}
                onChange={(e) => onChange('homepageUrl', e.target.value)}
                placeholder="https://www.company.co.kr"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 주소 정보 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon fontSize="small" color="primary" />
            주소 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="우편번호"
                value={companyInfo?.zipCd || ''}
                onChange={(e) => onChange('zipCd', e.target.value)}
                placeholder="00000"
                inputProps={{ maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                label="주소"
                value={companyInfo?.addr || ''}
                onChange={(e) => onChange('addr', e.target.value)}
                placeholder="서울특별시 강남구 테헤란로 123"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="상세주소"
                value={companyInfo?.addrDetail || ''}
                onChange={(e) => onChange('addrDetail', e.target.value)}
                placeholder="ABC빌딩 5층"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="주소 (영문)"
                value={companyInfo?.addrEn || ''}
                onChange={(e) => onChange('addrEn', e.target.value)}
                placeholder="5F, ABC Building, 123 Teheran-ro, Gangnam-gu, Seoul"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 은행 정보 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon fontSize="small" color="primary" />
            대표 계좌 정보
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="은행명"
                value={companyInfo?.bankNm || ''}
                onChange={(e) => onChange('bankNm', e.target.value)}
                placeholder="국민은행"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="계좌번호"
                value={companyInfo?.bankAccountNo || ''}
                onChange={(e) => onChange('bankAccountNo', e.target.value)}
                placeholder="123-456-789012"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="예금주"
                value={companyInfo?.bankAccountHolder || ''}
                onChange={(e) => onChange('bankAccountHolder', e.target.value)}
                placeholder="(주)회사명"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 견적서 기본 비고 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon fontSize="small" color="primary" />
            견적서 기본 비고
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            견적서 작성 시 비고란에 자동으로 채워지는 기본 내용입니다. 작성 후 개별 수정도 가능합니다.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={5}
                label="견적서 기본 비고"
                value={companyInfo?.qteDefaultDesc || ''}
                onChange={(e) => onChange('qteDefaultDesc', e.target.value)}
                placeholder={`-결제조건 :  현금\n-납기일자 :  발주 후 15일 이내.\n-견적유효 :  견적일로부터 30일\n-결제계좌 :  은행명 / 예금주(회사명) / 계좌번호\n-특기사항 :`}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 발주서 기본값 섹션: 견적서 기본 비고와 같은 패턴 - 작성 시 특기사항에 자동 반영, 개별 수정 가능 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon fontSize="small" color="primary" />
            발주서 기본값
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            발주서 작성 시 특기사항에 자동으로 채워지는 기본 내용입니다. 작성 후 개별 수정도 가능합니다.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="1. 결재(결제조건)"
                value={companyInfo?.spoDefaultPay || ''}
                onChange={(e) => onChange('spoDefaultPay', e.target.value)}
                placeholder="월말 마감 익월 말 현금"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="2. 납품(납기·장소)"
                value={companyInfo?.spoDefaultDlv || ''}
                onChange={(e) => onChange('spoDefaultDlv', e.target.value)}
                placeholder="발주 후 7일 이내 / 당사 지정 장소"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="3. 기타"
                value={companyInfo?.spoDefaultEtc || ''}
                onChange={(e) => onChange('spoDefaultEtc', e.target.value)}
                placeholder="검수 후 대금 지급"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 파일 업로드 섹션 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon fontSize="small" color="primary" />
            로고 및 인감
          </Typography>
          <Grid container spacing={3}>
            {/* 로고 업로드 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                border: '1px dashed', 
                borderColor: 'divider', 
                borderRadius: 2, 
                p: 2,
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  회사 로고
                </Typography>
                {logoBlobUrl ? (
                  <Box sx={{ mb: 2 }}>
                    <Avatar
                      variant="rounded"
                      src={logoBlobUrl}
                      sx={{ width: 120, height: 120, mx: 'auto' }}
                    >
                      <BusinessIcon sx={{ fontSize: 60 }} />
                    </Avatar>
                  </Box>
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: 'grey.100' }}
                  >
                    <BusinessIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                  </Avatar>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFileSelect(e, onLogoUpload)}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  size="small"
                >
                  로고 업로드
                </Button>
              </Box>
            </Grid>

            {/* 인감 업로드 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                border: '1px dashed', 
                borderColor: 'divider', 
                borderRadius: 2, 
                p: 2,
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  회사 인감
                </Typography>
                {sealBlobUrl ? (
                  <Box sx={{ mb: 2 }}>
                    <Avatar
                      variant="rounded"
                      src={sealBlobUrl}
                      sx={{ width: 120, height: 120, mx: 'auto' }}
                    >
                      <ImageIcon sx={{ fontSize: 60 }} />
                    </Avatar>
                  </Box>
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: 'grey.100' }}
                  >
                    <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                  </Avatar>
                )}
                <input
                  ref={sealInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFileSelect(e, onSealUpload)}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => sealInputRef.current?.click()}
                  size="small"
                >
                  인감 업로드
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            * 로고와 인감은 견적서, 발주서, 계약서 등에 자동으로 삽입됩니다.
          </Typography>
        </CardContent>
      </Card>

      {/* 푸터 */}
      {hasChanges && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="warning.main">
            ⚠️ 변경사항을 저장하지 않으면 손실됩니다.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CompanyInfoForm;
